import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { FlaskConical, Loader2, Sparkles, Trash2, ChevronDown, ChevronUp, Info } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const COPY = {
  portfolio: { noun: "sample schools", verb: "Add sample schools",
    desc: "Populate your portfolio with realistic sample schools and scored diagnostics so you can explore the reports while you test." },
  school: { noun: "sample diagnostics", verb: "Generate sample diagnostics",
    desc: "Create fully-scored sample diagnostic periods for your school so you can see how the reports and trends look." },
  sales: { noun: "demo leads", verb: "Add demo leads",
    desc: "Create demo leads in your pipeline so you can practice the workflow. They never count toward real revenue." },
};

// authFetch(path, options) must be supplied by the host portal (cookie vs header auth).
export default function SandboxPanel({ authFetch, onChanged }) {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(3);
  const [busy, setBusy] = useState("");

  const load = () => authFetch(`${API}/api/sandbox/status`)
    .then((r) => (r.ok ? r.json() : null)).then(setStatus).catch(() => setStatus(null));

  useEffect(() => { load(); }, []); // eslint-disable-line

  // Auto-expand whenever sample data exists so the Clear button is always
  // reachable (the panel can remount when the host dashboard reloads).
  useEffect(() => {
    const t = status ? Object.values(status.counts || {}).reduce((a, b) => a + b, 0) : 0;
    if (t > 0) setOpen(true);
  }, [status]);

  if (!status || !status.has_access) return null;
  const copy = COPY[status.context] || COPY.portfolio;
  const total = Object.values(status.counts || {}).reduce((a, b) => a + b, 0);

  const generate = async () => {
    setBusy("gen");
    try {
      const r = await authFetch(`${API}/api/sandbox/generate`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ count: Number(count) || 1 }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Failed");
      const made = Object.values(d.result || {})[0];
      toast.success(`Added ${made} ${copy.noun}`);
      setStatus((s) => ({ ...s, counts: d.counts }));
      onChanged && onChanged();
    } catch (e) { toast.error(e.message || "Could not generate sample data"); }
    setBusy("");
  };

  const reset = async () => {
    if (!window.confirm("Remove ALL sample/test data you've generated? This can't be undone.")) return;
    setBusy("reset");
    try {
      const r = await authFetch(`${API}/api/sandbox/reset`, { method: "POST" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Failed");
      toast.success("Sample data cleared");
      setStatus((s) => ({ ...s, counts: {} }));
      onChanged && onChanged();
    } catch (e) { toast.error(e.message || "Could not clear sample data"); }
    setBusy("");
  };

  return (
    <div className="rounded-2xl border border-indigo-200 bg-indigo-50/40 overflow-hidden" data-testid="sandbox-panel">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-4 py-3 text-left" data-testid="sandbox-toggle">
        <span className="flex items-center gap-2">
          <span className="h-8 w-8 rounded-lg bg-indigo-600 text-white flex items-center justify-center"><FlaskConical className="h-4 w-4" /></span>
          <span>
            <span className="block text-sm font-semibold text-slate-900">Testing Sandbox</span>
            <span className="block text-[11px] text-slate-500">{total > 0 ? `${total} sample records active` : "Generate sample data to explore the product"}</span>
          </span>
        </span>
        {open ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          <p className="text-sm text-slate-600">{copy.desc}</p>
          <div className="flex items-start gap-2 text-[11px] text-indigo-700 bg-white/70 rounded-lg px-3 py-2">
            <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>Sample data is clearly separated from real reporting and billing, and you can clear it anytime.</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2">
              <Input type="number" min={1} max={25} value={count} onChange={(e) => setCount(e.target.value)}
                className="w-20 h-9 bg-white" data-testid="sandbox-count" />
              <Button onClick={generate} disabled={busy === "gen"} className="h-9 bg-indigo-600 hover:bg-indigo-700" data-testid="sandbox-generate-btn">
                {busy === "gen" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />} {copy.verb}
              </Button>
            </div>
            {total > 0 && (
              <Button onClick={reset} disabled={busy === "reset"} variant="outline" className="h-9 text-red-600 border-red-200 hover:bg-red-50" data-testid="sandbox-reset-btn">
                {busy === "reset" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />} Clear sample data
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
